/**
 * HomePage — the Advisory login: the landing screen on every host but
 * localhost (and on localhost when switched to it for testing).
 *
 * A port of legacy's `Home` route (components/Home.vue + layout/TopBanner.vue):
 * the same three-part page — hero banner, the product card with the AMD Guest /
 * AMD Employee buttons, the register link — rendered in this app's dark theme.
 * Structure, copy and artwork are legacy's; colours, type, spacing and the
 * buttons come from the design system.
 *
 * Like legacy, it sits INSIDE the app shell (header + footer), not on a bare
 * backdrop: routes.jsx wraps it in AppLayout, and the header shows its
 * signed-out form (no section nav, notifications or profile menu).
 *
 * It lives at the landing URL ('/'; '/cca/' or '/eia/' locally) and is shown
 * there to EVERYONE, exactly as legacy: signed out, RequireAuth shows it (moving
 * any other path there first); signed in, it is the '/' route itself, with the
 * app bar above it listing the sections the account can open. Legacy keeps the
 * sign-in buttons on it for a signed-in user too (Home.vue only forwards on
 * '/home'), and so does this page — by the user's choice, "exactly like the
 * old tool". It never redirects anyone itself.
 *
 * It is NOT a form. Both buttons are a full-page redirect to the Common Services
 * app, which runs the SSO, writes the shared *.amd.com session cookies and sends
 * the browser back. On return, bootstrapAuth restores that session from the
 * cookies (security/auth/sessionMirror.js) before the first render, so the user
 * lands directly on the app — this page is never shown to them.
 *
 * Two buttons, one destination, exactly as legacy: "AMD Guest" and "AMD
 * Employee" both call the same oktalogin() (Home.vue:50-68, :163). The labels
 * only tell people which credentials Common Services will ask them for.
 *
 * Never an automatic redirect: if the round trip ever comes back with no
 * session (cookies blocked, SSO denied), an automatic redirect would bounce
 * app -> Common Services -> app forever, with no screen to land on.
 *
 * Default-exported for lazy loading.
 */
import { useEffect } from 'react';

import amdLogo from '@/assets/home/amd-logo-white.svg';
import epycWordmark from '@/assets/home/epyc-wordmark.svg';
import { Alert, Button } from '@components/ui';
import { env } from '@config/env';
import { redirectTo } from '@utils/navigation';
import { useAppMode } from '@variant/useAppMode';

import { buildSsoUrl, safeHttpUrl } from '../sso';

import styles from './HomePage.module.css';

export default function HomePage() {
  const { variant } = useAppMode();

  const ssoUrl = buildSsoUrl(env.csHomeUrl, variant.shortName);
  const registerUrl = safeHttpUrl(env.csRegisterUrl);

  // Legacy logs this and does nothing on click (Home.vue:169), leaving buttons
  // that appear to be broken. Say so on the page as well as in the console.
  useEffect(() => {
    if (!ssoUrl) {
      // eslint-disable-next-line no-console
      console.error(
        '[auth] The Advisory login has no Common Services URL. Set ' +
          'VITE_OKTA_POSTLOGOUT_REDIRECT_URI to an http(s) address.',
      );
    }
  }, [ssoUrl]);

  const signIn = () => redirectTo(ssoUrl);
  // "AMD Enterprise Cloud Instance Advisor" — legacy's `AMD {{ applicationTitle }}`.
  const title = `AMD ${variant.productName}`;

  return (
    <div className={styles.page}>
      {/*
        TopBanner.vue: artwork across the full width, the "Experience the cloud
        with AMD" lockup in the right-hand third.

        The lockup is COMPOSED rather than using legacy's explore-cca-img.png.
        That PNG has a white card baked into the raster, so it stamps a white
        rectangle over the photo and can never be recoloured. Rebuilt from the
        white AMD logo, the EPYC logotype (lifted from the app bar's own vector
        artwork) and live text, it is transparent, white, and sharp at any size —
        the same fix the Epyc Advisory portal's HeroBanner makes.
      */}
      <section className={styles.banner} aria-label="Experience the cloud with AMD EPYC">
        <div className={styles.lockup}>
          <p className={styles.lockupTitle}>Experience the cloud with AMD</p>
          <img className={styles.lockupLogo} src={amdLogo} alt="AMD" />
          <img className={styles.lockupEpyc} src={epycWordmark} alt="EPYC" />
        </div>
      </section>

      <div className={styles.content}>
        <section className={styles.card} aria-labelledby="home-title">
          <div className={styles.inner}>
            <h1 id="home-title" className={styles.title}>
              {title}
            </h1>
            <p className={styles.tagline}>{variant.tagline}</p>
            <p className={styles.lead}>Register today or login to explore the {title}</p>

            {ssoUrl ? null : (
              // "danger", not "error": the design system's Alert has only
              // info / success / warning / danger, and an unknown variant renders
              // as a plain box with no colour and no icon.
              <Alert variant="danger" role="alert" className={styles.alert}>
                Sign-in is not configured for this environment.
              </Alert>
            )}

            {/*
              Legacy lays the buttons out in one row and the explanations in the
              next, then nudges each button toward its explanation with fixed rem
              margins per breakpoint (Home.vue:228-247) — which never quite line
              up. Two real columns, each centred on one axis (button, then its
              text), keep every button exactly over its own explanation at any
              width, and stack cleanly on a narrow screen.

              Both buttons are `primary`: they are the page's one action, the
              same role the Epyc Advisory portal gives its primary launch button.
            */}
            <div className={styles.columns}>
              <div className={styles.column}>
                <Button
                  id="btn-amd-guest"
                  type="button"
                  variant="primary"
                  className={styles.choice}
                  disabled={!ssoUrl}
                  onClick={signIn}
                >
                  AMD Guest
                </Button>
                <p className={styles.explain}>
                  <strong>AMD Guest :</strong> login with SSO Account.
                </p>
                <p className={styles.access}>
                  Access requires prior authorization from your AMD account representative.
                  {registerUrl ? (
                    <>
                      <br />
                      Click link below to complete a registration and authorization request.
                    </>
                  ) : null}
                </p>
              </div>

              <div className={styles.column}>
                <Button
                  id="btn-amd-employee"
                  type="button"
                  variant="primary"
                  className={styles.choice}
                  disabled={!ssoUrl}
                  onClick={signIn}
                >
                  AMD Employee
                </Button>
                <p className={styles.explain}>
                  <strong>AMD Employee :</strong> Login with AMD Email (NTID@amd.com) and password
                </p>
              </div>
            </div>

            {/* Only when there is somewhere to go — legacy renders the link
                whatever the env says, and its sanitizeUrl turns a bad value
                into a dead `#`. */}
            {registerUrl ? (
              <p className={styles.register}>
                Don&apos;t have an account?{' '}
                <a href={registerUrl} rel="noopener noreferrer">
                  Register here
                </a>
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
